import {useMemo, useState} from "react";
import {ContainerClusterEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseContainerClusterDataTableFilterProps = Omit<UseDataTableFilterProps<ContainerClusterEntity>, 'filterConfig'>

export function useContainerClusterDataTableFilter(props: UseContainerClusterDataTableFilterProps): UseDataTableFilterResult<ContainerClusterEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [siteQuery, setSiteQuery] = useState<string | undefined>(undefined);
  const [siteDebouncedQuery] = useDebouncedValue(siteQuery, 200);

  const [afeQuery, setAfeQuery] = useState<string | undefined>(undefined);
  const [afeDebouncedQuery] = useDebouncedValue(afeQuery, 200);

  const filterConfig: DataTableFilterConfig<ContainerClusterEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "site": {
        setQuery: setSiteQuery,
        query: siteQuery,
        debouncedQuery: siteDebouncedQuery,
        filter: ({siteName}, query) => !siteName || !siteName.toLowerCase().includes(query.toLowerCase())
      },
      "afe": {
        setQuery: setAfeQuery,
        query: afeQuery,
        debouncedQuery: afeDebouncedQuery,
        filter: ({afe}, query) => !afe || !afe.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    siteQuery,
    siteDebouncedQuery,
    afeQuery,
    afeDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
